import { ChildBillBaseComponent, MonthlySettlementComponentDto } from 'types';
import { VatRateType } from 'types/settlementVatRates';

import { ChildrenBillComponentsTable } from 'components/templates/settlement/ChildrenBillPartsTable/ChildrenBillComponentsTable';
import { useMemo, useState } from 'react';
import { getFormattedPrice } from 'utils/getFormattedPrice';

type Props = {
    title?: string;
    diff?: number;
    isExpanded?: boolean;
    summary: number;
    costComponentList: MonthlySettlementComponentDto[];
    additionalLeftTableHeaderContent?: React.ReactNode;
};

export const ChildSettlementComponents = ({
    costComponentList,
    title,
    isExpanded = true,
    diff,
    summary,
    additionalLeftTableHeaderContent,
}: Props) => {
    const [isExpandedState, setIsExpandedState] = useState<boolean>(!!isExpanded);
    const billComponents = useMemo(() => {
        const components: ChildBillBaseComponent[] = [];

        costComponentList.forEach((component) => {
            if (component.grossSummary > 0) {
                const componentDiscount = component?.discountValue
                    ? `${component?.discountName} ${component?.discountValue} zł`
                    : '';
                const allDiscounts = component.rows
                    // @ts-ignore
                    .map((row) => row.discountName)
                    // @ts-ignore
                    .filter((name): name is string => !!name)
                    .join(', ');
                const finalDiscountString = [componentDiscount, allDiscounts]
                    .filter((str) => str)
                    .join(', ');

                components.push({
                    id: component.name.toLowerCase().replace(/\s+/g, '-'),
                    name: component.name,
                    grossAmount: component.grossSummary,
                    netAmount: component.netCostSummary,
                    vatAmount: component.vatSummary,
                    discountString: finalDiscountString ?? '-',
                    vatRate: VatRateType.VAT_23,
                    // @ts-ignore
                    additionalComponents: component.rows.map((row) => {
                        const getDiscountName = () => {
                            const discounts = [];

                            if (row.discountName) {
                                discounts.push(row.discountName);
                            }

                            if (row.categoryDiscountValue) {
                                discounts.push(
                                    `${row.categoryDiscountName} ${row.categoryDiscountValue} zł`,
                                );
                            }

                            return discounts.join(', ');
                        };

                        return {
                            id: row.name,
                            name: row.name,
                            amountString: `${row.units} x ${getFormattedPrice(
                                row.unitGrossPrice,
                            )} (${getFormattedPrice(row.grossSummary)})`,
                            netAmount: row.netCostSummary,
                            vatAmount: row.vatSummary,
                            grossAmount: row.grossSummary,
                            units: row.units,
                            unitGrossPrice: row.unitGrossPrice,
                            discountString: getDiscountName(),
                            vatRate: row.vatRate,
                        };
                    }),
                    onInvoice: component.onInvoice,
                });
            }
        });

        return components.length > 0 ? components : null;
    }, [costComponentList]);

    if (!billComponents) {
        return (
            <ChildrenBillComponentsTable
                onExpand={() => setIsExpandedState(!isExpandedState)}
                isExpanded={isExpandedState}
                billComponents={[]}
                title={title}
                summary={summary}
                diff={diff}
                additionalLeftTableHeaderContent={additionalLeftTableHeaderContent}
            />
        );
    }

    return (
        <ChildrenBillComponentsTable
            onExpand={() => setIsExpandedState(!isExpandedState)}
            isExpanded={isExpandedState}
            billComponents={billComponents}
            title={title}
            summary={summary}
            diff={diff}
            additionalLeftTableHeaderContent={additionalLeftTableHeaderContent}
        />
    );
};
